import { smoothCollapse, smoothExpand, resizeContainer, showToast } from './form-helpers.js';
import { deleteRegistration } from './tickets.api.js';
import { isGuestUser } from '../../http/utils.js';

// Safe fallback: __ may not be loaded yet on event-detail templates
const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };
const AUTH_DISABLED_ATTR = 'data-auth-disabled';

function disableMutatingControl(button) {
  if (!button) {
    return;
  }
  button.disabled = true;
  button.setAttribute('aria-disabled', 'true');
  button.setAttribute(AUTH_DISABLED_ATTR, '1');
  button.classList.add('opacity-60', 'cursor-not-allowed');
  if (!button.title) {
    button.title = __('Bitte einloggen, um diese Aktion zu nutzen.');
  }
}

function initGuestToggles(root) {
  root.querySelectorAll('.toggle-guest-form').forEach((button) => {
    if (button.dataset.toggleInit === '1') {
      return;
    }
    button.dataset.toggleInit = '1';
    button.addEventListener('click', () => {
      const form = document.getElementById(button.dataset.target);
      if (!form) {
        return;
      }
      if (!form.dataset.expanded) {
        smoothExpand(form);
        setTimeout(() => form.querySelector('input')?.focus(), 250);
      } else {
        smoothCollapse(form);
      }
    });
  });
}

function initConsentToggles(root) {
  root.querySelectorAll('.toggle-consent-btn').forEach((button) => {
    if (button.dataset.consentInit === '1') {
      return;
    }
    button.dataset.consentInit = '1';
    button.addEventListener('click', () => {
      const formId = button.dataset.formId || '';
      const box = formId ? document.getElementById(formId) : null;
      if (!box) {
        return;
      }
      if (!box.dataset.expanded) {
        smoothExpand(box);
        setTimeout(() => box.querySelector('input')?.focus(), 250);
      } else {
        smoothCollapse(box);
      }
    });
  });
}

export function initMembershipToggle(root) {
  root.querySelectorAll('form.register-form, form.register-guest-form').forEach((form) => {
    const checkbox = form.querySelector('input[name="has_membership_number"]');
    const wrapper = form.querySelector('.membership-number-wrap, [data-membership-wrapper]');
    const required = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
    const input = wrapper ? wrapper.querySelector('input[name="membership_number"]') : null;

    if (required) {
      if (checkbox) {
        checkbox.checked = true;
        checkbox.disabled = true;
        checkbox.setAttribute('aria-disabled', 'true');
        // Hide the entire checkbox row – it is meaningless when membership is required
        const checkboxLabel = checkbox.closest('.checkout-consent');
        if (checkboxLabel) checkboxLabel.classList.add('hidden');
      }
      if (wrapper) {
        wrapper.classList.remove('hidden');
      }
      if (input) {
        input.required = true;
      }
      form.querySelectorAll('.membership-optional').forEach((el) => el.classList.add('hidden'));
    }

    if (!checkbox || !wrapper || checkbox.dataset.toggleInit === '1') {
      return;
    }
    checkbox.dataset.toggleInit = '1';
    checkbox.addEventListener('change', () => {
      const show = checkbox.checked;
      wrapper.classList.toggle('hidden', !show);
      setTimeout(() => resizeContainer(form), 0);
    });
  });
}

function initDeleteActions(root, { softReload } = {}) {
  root.querySelectorAll('.delete-registration-btn').forEach((button) => {
    if (button.dataset.deleteInit === '1') {
      return;
    }
    button.dataset.deleteInit = '1';
    if (isGuestUser()) {
      disableMutatingControl(button);
      return;
    }
    button.addEventListener('click', async () => {
      const registrationId = button.dataset.deleteRegistration || button.dataset.reg;
      if (!registrationId) {
        return;
      }
      if (!await citaDangerConfirm(__('Registrierung löschen?'), __('Dein Ticket wird storniert.'))) {
        return;
      }
      try {
        const response = await deleteRegistration({ registration: registrationId });
        if (response?.success) {
          showToast(response?.toast || __('Registrierung gelöscht.'), 'success');
          if (typeof softReload === 'function') {
            setTimeout(() => {
              softReload();
            }, 400);
          } else {
            setTimeout(() => window.location.reload(), 700);
          }
        } else {
          const msg = response?.toast || __('Fehler beim Löschen.');
          showToast(msg, 'error');
        }
      } catch (error) {
        showToast(__('Es ist ein Fehler aufgetreten. Bitte versuch es später nochmal.'), 'error');
      }
    });
  });
}

export function initTicketList(root, options = {}) {
  if (!root) {
    return;
  }
  initGuestToggles(root);
  initConsentToggles(root);
  // initMembershipToggle is deferred to form expansion (openInlineCheckout)
  // to avoid 374 querySelectorAll + listeners for hidden forms.
  // Only init for already-expanded forms (preselect, deep-link).
  root.querySelectorAll('.ticket-checkout-inline[data-expanded="1"]').forEach((container) => {
    initMembershipToggle(container);
  });
  initDeleteActions(root, options);
}
